import { useCreateScreeningSession, useScreeningAnswer, useScreeningContact, useScreeningNext } from '@cannect/services/resources/screening/hooks'
import { buildScreeningDestinationPath, SCREENING_TOKEN_KEY } from '@cannect/services/resources/screening/requests'
import type { TScreeningNext, TWomanOption } from '@cannect/services/resources/screening/types'
import Button from 'new-components/Button'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { useEffect, useMemo, useRef, useState } from 'react'

const ATTENTION =
  'ATENÇÃO: Este direcionamento não substitui uma avaliação profissional e não realiza diagnóstico. Ele ajuda você a encontrar o melhor caminho dentro da Cannect.'

const WOMAN_OPTIONS: TWomanOption[] = [
  { slug: 'menopausa', name: 'Menopausa / Climatério' },
  { slug: 'tpm', name: 'TPM / TDPM' },
  { slug: 'colica', name: 'Cólica menstrual' },
  { slug: 'fsfi', name: 'Função sexual' }
]

const QuestionOptions = ({
  next,
  onPick,
  disabled
}: {
  next: TScreeningNext
  onPick: (value: unknown) => void
  disabled: boolean
}) => {
  const question = next.question
  if (!question) return null

  const options = Array.isArray(question.options) ? question.options : null
  const scale = question.options && !Array.isArray(question.options) ? (question.options as Record<string, unknown>) : null

  if (question.type === 'date') {
    return (
      <DateField
        onConfirm={(value) => onPick(value)}
        disabled={disabled}
      />
    )
  }

  if (question.type === 'text' || question.type === 'phone' || question.type === 'email' || question.type === 'num') {
    return (
      <TextField
        type={question.type}
        placeholder={typeof scale?.ph === 'string' ? scale.ph : ''}
        onConfirm={(value) => onPick(value)}
        disabled={disabled}
      />
    )
  }

  if (question.type === 'check' && scale?.groups) {
    return <CheckField groups={scale.groups as { title?: string; items: string[] }[]} noneLabel={String(scale.noneLabel || '')} onConfirm={onPick} disabled={disabled} />
  }

  if ((question.type === 'nrs' || question.type === 'slider' || question.type === 'cat') && scale) {
    const min = Number(scale.min ?? 0)
    const max = Number(scale.max ?? 10)
    const values = Array.from({ length: max - min + 1 }, (_, i) => min + i)
    return (
      <div className="flex flex-col gap-3">
        <p className="text-xs text-neutral-600">
          {String(scale.lo || scale.left || min)} — {String(scale.hi || scale.right || max)}
        </p>
        <div className="flex flex-wrap gap-2">
          {values.map((value) => (
            <button
              key={value}
              type="button"
              disabled={disabled}
              onClick={() => onPick(value)}
              className="min-w-10 rounded-lg border border-neutral-200 px-3 py-2 text-sm hover:border-primary-700">
              {value}
            </button>
          ))}
        </div>
      </div>
    )
  }

  if (options) {
    return (
      <div className="flex flex-col gap-2">
        {options.map((option) => (
          <button
            key={String(option.v)}
            type="button"
            disabled={disabled}
            onClick={() => onPick(option.v)}
            className="rounded-lg border border-neutral-200 px-4 py-3 text-left text-sm hover:border-primary-700">
            {option.l}
          </button>
        ))}
      </div>
    )
  }

  return <TextField type="text" onConfirm={(value) => onPick(value)} disabled={disabled} />
}

const TextField = ({
  type,
  placeholder,
  onConfirm,
  disabled
}: {
  type: string
  placeholder?: string
  onConfirm: (value: string) => void
  disabled: boolean
}) => {
  const [value, setValue] = useState('')
  return (
    <div className="flex flex-col gap-3">
      <input
        className="rounded-lg border border-neutral-200 px-3 py-2"
        type={type === 'email' ? 'email' : type === 'num' ? 'number' : type === 'phone' ? 'tel' : 'text'}
        placeholder={placeholder}
        value={value}
        onChange={(event) => setValue(event.target.value)}
      />
      <Button disabled={disabled || !value.trim()} onClick={() => onConfirm(value)}>
        Continuar
      </Button>
    </div>
  )
}

const DateField = ({ onConfirm, disabled }: { onConfirm: (value: string) => void; disabled: boolean }) => {
  const [value, setValue] = useState('')
  return (
    <div className="flex flex-col gap-3">
      <input
        className="rounded-lg border border-neutral-200 px-3 py-2"
        type="date"
        value={value}
        max={new Date().toISOString().slice(0, 10)}
        onChange={(event) => setValue(event.target.value)}
      />
      <Button disabled={disabled || !value} onClick={() => onConfirm(value)}>
        Continuar
      </Button>
    </div>
  )
}

const CheckField = ({
  groups,
  noneLabel,
  onConfirm,
  disabled
}: {
  groups: { title?: string; items: string[] }[]
  noneLabel: string
  onConfirm: (value: string[]) => void
  disabled: boolean
}) => {
  const [selected, setSelected] = useState<string[]>([])
  return (
    <div className="flex flex-col gap-3">
      {groups.map((group) => (
        <div key={group.title || 'g'}>
          {group.title ? <p className="mb-1 text-xs uppercase text-neutral-500">{group.title}</p> : null}
          <div className="flex flex-wrap gap-2">
            {group.items.map((item) => {
              const active = selected.includes(item)
              return (
                <button
                  key={item}
                  type="button"
                  className={`rounded-full border px-3 py-1 text-xs ${active ? 'border-primary-700 bg-primary-50' : 'border-neutral-200'}`}
                  onClick={() =>
                    setSelected((curr) => (curr.includes(item) ? curr.filter((x) => x !== item) : [...curr, item]))
                  }>
                  {item}
                </button>
              )
            })}
          </div>
        </div>
      ))}
      <Button disabled={disabled} onClick={() => onConfirm(selected)}>
        {selected.length ? 'Continuar' : noneLabel || 'Continuar'}
      </Button>
    </div>
  )
}

export const PathologyScreening = () => {
  const history = useHistory()
  const location = useLocation<{ heroSlug?: string; womanOptions?: TWomanOption[] }>()
  const { token: paramToken } = useParams<{ token?: string }>()
  const pathToken = location.pathname.replace(/^\/avaliacao\/?/, '').split(/[/?#]/)[0]
  const routeToken = paramToken || pathToken || ''
  const heroSlug = useMemo(() => new URLSearchParams(location.search).get('hero') || location.state?.heroSlug || '', [location])
  const [token, setToken] = useState(routeToken || sessionStorage.getItem(SCREENING_TOKEN_KEY) || '')
  const [womanPick, setWomanPick] = useState(false)
  const [startError, setStartError] = useState('')
  const startedRef = useRef(false)
  const createSession = useCreateScreeningSession()
  const { data: next, isLoading, isError: nextFailed } = useScreeningNext(token || undefined)
  const contactMutation = useScreeningContact(token)
  const answerMutation = useScreeningAnswer(token)
  const busy = contactMutation.isPending || answerMutation.isPending || createSession.isPending

  useEffect(() => {
    if (routeToken && routeToken !== token) setToken(routeToken)
  }, [routeToken, token])

  useEffect(() => {
    if (token || routeToken || !heroSlug || startedRef.current) return
    if (heroSlug === 'mulher') {
      setWomanPick(true)
      return
    }
    startedRef.current = true
    createSession.mutate(
      { heroSlug },
      {
        onSuccess: (response) => {
          if (!response.success || !response.data?.token) {
            startedRef.current = false
            setStartError('Não foi possível iniciar a avaliação.')
            return
          }
          sessionStorage.setItem(SCREENING_TOKEN_KEY, response.data.token)
          setToken(response.data.token)
          setStartError('')
          history.replace(`/avaliacao/${response.data.token}?hero=${heroSlug}`)
        },
        onError: (error: { response?: { data?: { data?: { options?: TWomanOption[] }; message?: string } } }) => {
          const options = error?.response?.data?.data?.options
          if (options?.length) {
            setWomanPick(true)
            return
          }
          startedRef.current = false
          setStartError(
            error?.response?.data?.message || 'Não foi possível iniciar a avaliação. Tente novamente.'
          )
        }
      }
    )
  }, [heroSlug, token, createSession, history])

  useEffect(() => {
    if (!next?.done || !token) return
    sessionStorage.setItem(SCREENING_TOKEN_KEY, token)
    history.replace(buildScreeningDestinationPath(next, token))
  }, [next, token, history])

  const startWoman = (slug: string) => {
    createSession.mutate(
      { heroSlug: heroSlug || 'mulher', moduleSlug: slug },
      {
        onSuccess: (response) => {
          if (!response.success || !response.data?.token) return
          sessionStorage.setItem(SCREENING_TOKEN_KEY, response.data.token)
          setWomanPick(false)
          setToken(response.data.token)
          history.replace(`/avaliacao/${response.data.token}?hero=mulher`)
        }
      }
    )
  }

  const submit = (value: unknown) => {
    if (!next?.question || !token) return
    const key = next.question.key
    if (next.phase === 'symptoms') {
      answerMutation.mutate({ key, value })
      return
    }
    contactMutation.mutate({ [key]: value })
  }

  return (
    <div className="mx-auto flex min-h-[70vh] max-w-xl flex-col gap-6 px-4 py-10">
      <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">{ATTENTION}</p>

      {womanPick && !token ? (
        <div className="flex flex-col gap-3">
          <h1 className="text-xl">Qual triagem de saúde da mulher você quer iniciar?</h1>
          {WOMAN_OPTIONS.map((option) => (
            <button
              key={option.slug}
              type="button"
              className="rounded-lg border border-neutral-200 px-4 py-3 text-left hover:border-primary-700"
              onClick={() => startWoman(option.slug)}>
              {option.name}
            </button>
          ))}
        </div>
      ) : null}

      {!womanPick && startError ? (
        <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{startError}</p>
      ) : null}

      {!womanPick && nextFailed ? (
        <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          Não foi possível carregar a próxima pergunta.
        </p>
      ) : null}

      {!womanPick && !startError && (isLoading || createSession.isPending) ? <p>Carregando…</p> : null}

      {!womanPick && next?.question ? (
        <div className="flex flex-col gap-4">
          <h1 className="text-xl">{next.question.text}</h1>
          {next.question.sub_text ? <p className="text-sm text-neutral-600">{next.question.sub_text}</p> : null}
          <QuestionOptions key={next.question.key} next={next} onPick={submit} disabled={busy} />
        </div>
      ) : null}
    </div>
  )
}
